import {defer, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import invariant from 'tiny-invariant';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {seoPayload} from '~/lib/seo.server';
import {
  aggregateFields,
  findValueFromFields,
  getSiteDetails,
  processPageBlocks,
} from '~/utils';
import ComponentUtil from '~/components/ComponentUtil';
import {useGetContext} from '~/hooks/useGetContext';
import NavbarSection from '~/components/Sections/NavbarSection';
import {fetchSite} from '~/lib/storefrontFetcher';
import * as _ from 'lodash';

export const headers = routeHeaders;

export async function loader({request, params, context}: LoaderArgs) {
  invariant(params.pageHandle, 'Missing page handle');
  const site = await fetchSite({context});

  const {pages, articles, product_collections, faqs} = await getSiteDetails(
    context,
    site,
  );

  const page = _.find(pages, (it) => _.get(it, 'handle') == params.pageHandle);
  if (!page) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const {blocks, collectionProducts, allArticles, allFaqs} =
    await processPageBlocks({
      context,
      page,
      articles,
      product_collections,
      faqs,
    });
  const {storefront} = context;
  const {language, country} = storefront.i18n;

  if (!page) {
    throw new Response(null, {status: 404});
  }

  const seo = seoPayload.page({page, url: request.url});

  return defer(
    {
      page,
      seo,
      articles: aggregateFields(allArticles),
      collectionProducts,
      language,
      country,
      allFaqs,
      blocks,
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  const {
    page,
    collectionProducts,
    articles,
    language,
    country,
    allFaqs,
    blocks,
  } = useLoaderData<typeof loader>();
  const {menu, logo, isAuthenticated} = useGetContext();
  return (
    <>
      <NavbarSection
        menu={menu}
        headerStyle={_.get(page, 'style')}
        logo={logo || ''}
        title={_.get(page, 'title') || ''}
        isAuthenticated={isAuthenticated}
      />
      <ComponentUtil
        pageSections={[]}
        title={findValueFromFields(page, 'name')}
        collectionProducts={collectionProducts}
        articles={articles}
        language={language}
        country={country}
        allFaqs={allFaqs}
        blocks={blocks}
      />
      <div className="container">
        <div className="row"></div>
      </div>
    </>
  );
}
